import { useState } from 'react';
import {
	Crown,
	HeartPulse,
	Sparkles,
	Sprout,
	type LucideIcon,
} from 'lucide-react';
import { cn } from '@/lib/utils';

interface ClubIconProps {
	club: Pick<Clubs.Club, 'icon_img_url' | 'name'>;
	className?: string;
	size?: 'sm' | 'md' | 'lg';
}

const sizeClasses = {
	sm: 'h-8 w-8',
	md: 'h-10 w-10',
	lg: 'h-12 w-12',
};

const iconSizes = { sm: 16, md: 20, lg: 28 };

type DefaultClubIcon = { icon: LucideIcon; className: string };

/**
 * Ícone padrão por nome do plano, usado quando o admin ainda não subiu uma
 * imagem (icon_img_url vazio). Casa por palavra-chave, sem acento.
 */
const DEFAULT_CLUB_ICONS: Array<{ match: RegExp } & DefaultClubIcon> = [
	{
		match: /essencial|basic|inicial|start/,
		icon: Sprout,
		className: 'bg-emerald-100 text-emerald-700',
	},
	{
		match: /saude|bem-?estar|cuidado|health/,
		icon: HeartPulse,
		className: 'bg-rose-100 text-rose-600',
	},
	{
		match: /completo|premium|total|plus|full/,
		icon: Crown,
		className: 'bg-amber-100 text-amber-700',
	},
];

const FALLBACK_CLUB_ICON: DefaultClubIcon = {
	icon: Sparkles,
	className: 'bg-brand-orange/10 text-brand-orange',
};

const normalize = (value: string) =>
	value.toLocaleLowerCase('pt-BR').normalize('NFD').replace(/[̀-ͯ]/g, '');

export const getDefaultClubIcon = (name?: string | null): DefaultClubIcon => {
	const key = normalize(name ?? '');
	return (
		DEFAULT_CLUB_ICONS.find((entry) => entry.match.test(key)) ??
		FALLBACK_CLUB_ICON
	);
};

export const ClubIcon = ({ club, className, size = 'md' }: ClubIconProps) => {
	const hasUrl = !!club?.icon_img_url;
	const [imgError, setImgError] = useState(false);
	const fallback = getDefaultClubIcon(club?.name);
	const FallbackIcon = fallback.icon;

	return (
		<div
			className={cn(
				'flex flex-shrink-0 items-center justify-center overflow-hidden rounded-xl bg-stone-100',
				sizeClasses[size],
				className,
			)}
		>
			{hasUrl && !imgError ? (
				<img
					src={club.icon_img_url ?? ''}
					alt={club.name}
					className="h-full w-full object-cover object-center"
					onError={() => setImgError(true)}
				/>
			) : (
				<div
					className={cn(
						'flex size-full items-center justify-center',
						fallback.className,
					)}
					aria-label={club?.name}
				>
					<FallbackIcon size={iconSizes[size]} strokeWidth={2.25} />
				</div>
			)}
		</div>
	);
};
